import { useDeepEffect, useSetState } from '@domain-group/react-hooks'

import { digObject, toggleArray } from '@domain-group/javascript-utils'

type DefaultState = {
  selectedEntityIds: number[],
}

type GetSelectableListItemPropsParams = {
  entityId: number,
  setState: (state: Partial<DefaultState>) => void,
  state: DefaultState,
}

const getSelectableListItemProps = (params: GetSelectableListItemPropsParams) => {
  const { entityId, setState, state } = params
  const { selectedEntityIds } = state

  const toggleId = () => setState({ selectedEntityIds: toggleArray(selectedEntityIds, entityId) })

  return {
    checkboxOnClick: toggleId,
    isChecked: selectedEntityIds.includes(entityId),
  }
}

export type ListItemProps = ReturnType<typeof getSelectableListItemProps>

type Options = {
  initSelectedIds?: number[],
  minimumSelected?: number,
}

const defaultState: DefaultState = {
  selectedEntityIds: [],
}

function useSelectEntities(options: Options = {}) {
  const [state, setState] = useSetState(defaultState)
  const { selectedEntityIds } = state

  const { initSelectedIds } = options

  useDeepEffect(() => {
    if (initSelectedIds){
      setState({ selectedEntityIds: initSelectedIds })
    }
  }, [initSelectedIds])

  const minimumSelected = digObject(options, 'minimumSelected', 1)

  return {
    callbacks: {
      deselectAll: () => setState({ selectedEntityIds: [] }),
      getSelectableListItemProps: (entityId: number) => getSelectableListItemProps({ entityId, setState, state }),
      isChecked: (entityId: number) => selectedEntityIds.includes(entityId),
      selectAllIds: (entityIds: number[]) => setState({ selectedEntityIds: entityIds }),
      selectAll: (
        entityArray: any[],
      ) => setState({ selectedEntityIds: entityArray.map(entity => entity.id) }),
      setSelectedIds: (idsArray: number[]) => setState({ selectedEntityIds: idsArray }),
      toggleId: (entityId: number) => setState({ selectedEntityIds: toggleArray(selectedEntityIds, entityId) }),
    },
    hasMinimumSelected: selectedEntityIds.length >= minimumSelected,
    hasSelectedEntities: !!selectedEntityIds.length,
    selectedEntitiesCount: selectedEntityIds.length,
    selectedEntityIds,
  }
}

export type UseSelectEntitiesPayload = ReturnType<typeof useSelectEntities>
export default useSelectEntities
